import { useMemo, useRef, useState } from "react";
import type { Validator } from "./validators";

type Validators<T> = {
  [K in keyof T]?: Validator<T[K]>[];
};

export function useFormValidation<T extends Record<string, any>>(
  values: T,
  rules: Validators<T>,
) {
  const [touched, setTouched] = useState<Partial<Record<keyof T, boolean>>>({});
  const [shake, setShake] = useState(false);
  const submitAttempted = useRef(false);

  const errors = useMemo(() => {
    const result: Partial<Record<keyof T, string>> = {};
    for (const key in rules) {
      const validators = rules[key];
      if (!validators) continue;

      for (const validate of validators) {
        const message = validate(values[key]);
        if (message) {
          result[key] = message;
          break;
        }
      }
    }
    return result;
  }, [values, rules]);

  const isValid = Object.keys(errors).length === 0;
  const firstErrorField =
    (Object.keys(errors)[0] as keyof T | undefined) ?? null;

  /* ---------- touched helpers ---------- */
  const markTouched = (field: keyof T) =>
    setTouched((t) => ({ ...t, [field]: true }));

  const markAllTouched = () => {
    submitAttempted.current = true;
    const all: Partial<Record<keyof T, boolean>> = {};
    Object.keys(rules).forEach((k) => {
      all[k as keyof T] = true;
    });
    setTouched(all);

    // trigger shake once per submit
    setShake(true);
    setTimeout(() => setShake(false), 450);
  };

  const shouldShowError = (field: keyof T) =>
    touched[field] || submitAttempted.current;

  const focusFirstError = (
    refs: Partial<Record<keyof T, React.RefObject<HTMLElement | null>>>,
  ) => {
    if (!firstErrorField) return;
    refs[firstErrorField]?.current?.focus();
  };

  return {
    errors,
    isValid,
    shake,
    firstErrorField,
    markTouched,
    markAllTouched,
    shouldShowError,
    focusFirstError,
  };
}
